"use client";
import PropTypes from "prop-types";
import { useRouter } from "next/navigation";
import Breadcrumb from "../../components/atoms/breadCrumb";
import Typography from "../../components/atoms/typography";
import { CUSTOMER_REVIEWS_N_TESTIMONIALS, WHAT_OUR_USERS_SAY_ABOUT_US } from "../../constants/common/common";
import RateAndReview from "../../components/molecules/rateAndReview";
import TestimonialsHeader from "./testimonialsComponents/TestimonialsHeader";
import Loader from "../../components/atoms/loader";
import { useEffect, useState } from "react";
import { getTestimonialsData } from "../../api/testimonialsApi";
import { getMonthNumber, restrictAutoScrollingOnLoad } from "../../utils/common";
import { PageTypes, setPageType } from "../../utils/fnpPageType";
import useCommonOmnitureSetup from "../../customHooks/omniture/useCommonOmnitureSetup";
import Pagination from "./testimonialsComponents/Pagination";
import TestimonialCard from "../../components/organisms/testimonialCard";

/** */
const TestimonialsPage = ({
  responseData,
  breadcrumbs,
  isMobile,
  catalogId,
  isShowSchema,
  pageType = "",
  ratingValue,
  reviewCount,
  selectedYear,
  selectedMonth,
}) => {
  const router = useRouter();
  const [currentPage, setCurrentPage] = useState(responseData?.currentPage);
  const [isShowLoading, setIsShowLoading] = useState(false);
  const [testimonialReviews, setTestimonialReviews] = useState(responseData?.data ?? []);
  const review = {};
  review["ratingValue"] = ratingValue;

  useEffect(() => {
    restrictAutoScrollingOnLoad();
    setPageType(PageTypes.TESTIMONIALS);
  }, []);

  useCommonOmnitureSetup();

  /** */
  const handleMonthYear = (year, month) => {
    router.push(`/info/testimonials/${year}/${month.toLowerCase()}`);
    setTestimonialReviews([]);
    setIsShowLoading(true);
  };
  /** */
  const onHandlePageChange = async (newPage) => {
    setIsShowLoading(true);
    setTestimonialReviews([]);
    const searchQuery = window.location.search;

    const testimonialOptions = {
      pageType: pageType || "dateRange",
      requestType: "csr",
      pageNumber: newPage - 1,
    };
    if (pageType === "category" || pageType === "product") {
      const searchParams = new URLSearchParams(searchQuery);
      const params = {};
      searchParams.forEach((value, key) => {
        params[key] = value;
      });
      testimonialOptions["categoryId"] = params?.categoryId;
      testimonialOptions["productId"] = params?.productId;
    } else if (selectedMonth && selectedYear) {
      testimonialOptions["month"] = getMonthNumber(selectedMonth);
      testimonialOptions["year"] = selectedYear;
    }

    setCurrentPage(newPage);
    const response = await getTestimonialsData(testimonialOptions);
    setTestimonialReviews(response?.data);
    setIsShowLoading(false);
  };
  return (
    <>
      <div className="px-16 md:px-40">
        {isShowLoading && <Loader />}
        {breadcrumbs && !isMobile && (
          <Breadcrumb items={breadcrumbs} catalogId={catalogId} isShowSchema={isShowSchema} />
        )}

        <div className="my-20 flex flex-wrap justify-between md:mt-0 md:flex-nowrap">
          <div className="flex flex-col md:gap-16">
            <div className="flex flex-col justify-start gap-12 md:flex-row md:items-center">
              <Typography Tag={"h2"} variant="h2" classes="text-14 md:text-24 text-fnp-500 font-500">
                {CUSTOMER_REVIEWS_N_TESTIMONIALS}
              </Typography>
              {(pageType === "category" || pageType === "product") && (
                <div className="flex items-center gap-8">
                  <RateAndReview
                    review={review}
                    isReviewTextHide={true}
                    isShowTotalReviews={false}
                    classes="static border"
                  />
                  <span className="text-12 font-500 text-black-100 md:text-16">{reviewCount} Reviews</span>
                </div>
              )}
            </div>
            {!isMobile && <div className="text-20 font-500 text-black-100">{WHAT_OUR_USERS_SAY_ABOUT_US}</div>}
          </div>
          {pageType !== "category" && pageType !== "product" && (
            <div className="w-full md:w-auto">
              <TestimonialsHeader
                pageType={pageType}
                testimonialsCount={testimonialReviews?.length}
                handleMonthYear={handleMonthYear}
                year={selectedYear}
                month={selectedMonth}
                currentPage={currentPage}
                onHandlePageChange={onHandlePageChange}
                totalPages={responseData?.totalPages}
                isMobile={isMobile}
              />
            </div>
          )}
        </div>

        <div className="flex w-full flex-col gap-20">
          {testimonialReviews?.map((item) => {
            return <TestimonialCard review={item} key={item?.respondedDateMillis} isShowImage={true} />;
          })}
        </div>
      </div>
      <div className="mt-20 flex items-center justify-center border-t-1 border-thinLightGrey pt-20">
        {responseData.data.length > 0 && (
          <Pagination
            noOfPages={responseData?.totalPages}
            page={currentPage}
            defaultPage={1}
            onChange={onHandlePageChange}
            isMobile={isMobile}
            customClasses={{ container: "border rounded-8 overflow-hidden" }}
          />
        )}
      </div>
    </>
  );
};

TestimonialsPage.propTypes = {
  responseData: PropTypes.object.isRequired,
  breadcrumbs: PropTypes.array,
  isMobile: PropTypes.bool.isRequired,
  catalogId: PropTypes.string.isRequired,
  isShowSchema: PropTypes.bool.isRequired,
  pageType: PropTypes.string,
  ratingValue: PropTypes.number,
  reviewCount: PropTypes.number,
  selectedYear: PropTypes.string,
  selectedMonth: PropTypes.string,
};
export default TestimonialsPage;
